import React, { useEffect, useState } from "react";
import { ShieldCheck, Lock, Plus, Trash2, Save, KeyRound } from "lucide-react";
import { api, apiError, gate, gateHeaders } from "@/lib/apiClient";
import { PageHeader, Loader } from "@/components/common";
import { ImageUpload } from "@/pages/admin/AdminUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const MODULE_LABELS = {
  dashboard: "Dashboard", students: "Students", courses: "Courses", reviews: "Reviews",
  jobs: "Jobs", messages: "Messages", content: "Homepage & Content", community: "Community",
  products: "Products & Access", bookings: "Bookings", analytics: "Analytics",
};

function GatePrompt({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const unlock = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/owner/unlock", { password: pw }); gate.set(data.gate_token); onUnlock(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto mt-10 bg-white border border-border rounded-2xl shadow-sm p-8 text-center" data-testid="owner-gate">
      <div className="w-16 h-16 rounded-2xl bg-teal/10 flex items-center justify-center mx-auto mb-4"><Lock className="w-8 h-8 text-teal" /></div>
      <h2 className="font-heading text-xl font-bold">Owner Verification</h2>
      <p className="text-sm text-muted-foreground mt-1">Enter your owner password to access payment & staff controls.</p>
      <Input type="password" className="mt-4" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && unlock()} placeholder="Owner password" data-testid="owner-gate-input" />
      <Button className="rounded-full bg-teal hover:bg-teal/90 w-full mt-3" onClick={unlock} disabled={busy || !pw} data-testid="owner-gate-submit"><ShieldCheck className="w-4 h-4 mr-1.5" /> Unlock</Button>
      <p className="text-xs text-muted-foreground mt-3">Default password is <span className="font-mono">Owner@Reyland2026</span> — change it in the Security tab.</p>
    </div>
  );
}

const emptyMethod = { label: "", type: "qr", qr_url: null, details: "", active: true };

function PaymentMethods({ onExpired }) {
  const [methods, setMethods] = useState(null);
  const [instructions, setInstructions] = useState("");
  const load = async () => {
    try { const { data } = await api.get("/admin/payment-settings", gateHeaders()); setMethods(data.methods || []); setInstructions(data.instructions || ""); }
    catch (e) { if (e?.response?.status === 403) onExpired(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const setM = (i, k, v) => { const m = [...methods]; m[i] = { ...m[i], [k]: v }; setMethods(m); };
  const save = async () => {
    try { await api.put("/admin/payment-settings", { methods, instructions }, gateHeaders()); toast.success("Payment settings saved"); }
    catch (e) { if (e?.response?.status === 403) onExpired(); else toast.error(apiError(e)); }
  };
  if (!methods) return <Loader />;
  return (
    <div className="space-y-4" data-testid="payment-methods">
      <div className="bg-white border border-border rounded-2xl p-6 shadow-sm space-y-3">
        <Label>General payment instructions (shown to students at checkout)</Label>
        <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. After paying, upload your receipt and we'll verify within 24 hours." data-testid="pay-instructions" />
      </div>
      {methods.map((m, i) => (
        <div key={i} className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3" data-testid={`pay-method-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1"><Label>Method name</Label><Input value={m.label} onChange={(e) => setM(i, "label", e.target.value)} placeholder="e.g. GCash / Maya / BPI" data-testid={`pay-label-${i}`} /></div>
            <div className="w-36"><Label>Type</Label>
              <Select value={m.type} onValueChange={(v) => setM(i, "type", v)}>
                <SelectTrigger data-testid={`pay-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="qr">QR Code</SelectItem><SelectItem value="bank">Bank / E-wallet</SelectItem><SelectItem value="instructions">Instructions</SelectItem></SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="ghost" className="mt-5" onClick={() => setMethods(methods.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-red-500" /></Button>
          </div>
          {m.type === "qr" && <ImageUpload label="QR code image" value={m.qr_url} onChange={(v) => setM(i, "qr_url", v)} testId={`pay-qr-${i}`} />}
          <div><Label>Details (account number, name, notes)</Label><Textarea value={m.details} onChange={(e) => setM(i, "details", e.target.value)} data-testid={`pay-details-${i}`} /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={m.active !== false} onCheckedChange={(v) => setM(i, "active", v)} data-testid={`pay-active-${i}`} /> Active (visible to students)</label>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" className="rounded-full" onClick={() => setMethods([...methods, { ...emptyMethod }])} data-testid="add-pay-method"><Plus className="w-4 h-4 mr-1.5" /> Add Method</Button>
        <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-pay-settings"><Save className="w-4 h-4 mr-1.5" /> Save</Button>
      </div>
    </div>
  );
}

function StaffPermissions({ onExpired }) {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/staff-permissions", gateHeaders()); setData(data); }
    catch (e) { if (e?.response?.status === 403) onExpired(); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const toggle = async (s, mod, val) => {
    const perms = { ...s.permissions, [mod]: val };
    try { await api.put(`/admin/staff-permissions/${s.id}`, { permissions: perms }, gateHeaders()); setData((d) => ({ ...d, staff: d.staff.map((x) => x.id === s.id ? { ...x, permissions: perms } : x) })); }
    catch (e) { if (e?.response?.status === 403) onExpired(); else toast.error(apiError(e)); }
  };
  if (!data) return <Loader />;
  return (
    <div className="space-y-4" data-testid="staff-permissions">
      {data.staff.map((s) => (
        <div key={s.id} className="bg-white border border-border rounded-2xl p-5 shadow-sm" data-testid={`perm-staff-${s.id}`}>
          <div className="flex items-center gap-2 mb-3">
            <p className="font-medium">{s.name || s.email}</p>
            <span className="text-xs text-muted-foreground">{s.email} · {s.role}</span>
            {s.is_owner && <span className="text-xs font-semibold text-teal bg-teal/10 px-2 py-0.5 rounded-full">Owner</span>}
          </div>
          {s.is_owner ? <p className="text-sm text-muted-foreground">The owner always has full access to every module.</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {data.modules.map((mod) => (
                <label key={mod} className="flex items-center justify-between gap-2 text-sm p-2 rounded-lg bg-secondary/40">
                  <span>{MODULE_LABELS[mod] || mod}</span>
                  <Switch checked={s.permissions?.[mod] !== false} onCheckedChange={(v) => toggle(s, mod, v)} data-testid={`perm-${s.id}-${mod}`} />
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
      {data.staff.length === 0 && <p className="text-muted-foreground text-sm">No staff accounts yet. Invite staff from the Staff page.</p>}
    </div>
  );
}

function Security({ onExpired }) {
  const [form, setForm] = useState({ current: "", new: "", confirm: "" });
  const save = async () => {
    if (form.new !== form.confirm) return toast.error("New passwords do not match");
    try { await api.post("/admin/owner/change-password", { current: form.current, new: form.new }, gateHeaders()); toast.success("Owner password updated"); setForm({ current: "", new: "", confirm: "" }); }
    catch (e) { if (e?.response?.status === 403) onExpired(); else toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-md bg-white border border-border rounded-2xl p-6 shadow-sm space-y-3" data-testid="owner-security">
      <h3 className="font-heading font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-teal" /> Change Owner Password</h3>
      <div><Label>Current password</Label><Input type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} data-testid="owner-cur-pw" /></div>
      <div><Label>New password</Label><Input type="password" value={form.new} onChange={(e) => setForm({ ...form, new: e.target.value })} data-testid="owner-new-pw" /></div>
      <div><Label>Confirm new password</Label><Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="owner-confirm-pw" /></div>
      <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-owner-pw">Update Password</Button>
    </div>
  );
}

export default function AdminOwner() {
  const [unlocked, setUnlocked] = useState(!!gate.get());
  const expire = () => { gate.set(null); setUnlocked(false); toast.error("Owner session expired — please verify again"); };
  return (
    <div>
      <PageHeader title="Owner Controls" subtitle="Payment settings, staff permissions & security — owner only" />
      {!unlocked ? <GatePrompt onUnlock={() => setUnlocked(true)} /> : (
        <Tabs defaultValue="payments">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="payments" data-testid="otab-payments">Payment Settings</TabsTrigger>
            <TabsTrigger value="permissions" data-testid="otab-permissions">Staff Permissions</TabsTrigger>
            <TabsTrigger value="security" data-testid="otab-security">Security</TabsTrigger>
          </TabsList>
          <TabsContent value="payments"><PaymentMethods onExpired={expire} /></TabsContent>
          <TabsContent value="permissions"><StaffPermissions onExpired={expire} /></TabsContent>
          <TabsContent value="security"><Security onExpired={expire} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
